import { commonComponents as cc } from "@/components/common";
import AuthWrap from "@/features/auth/components/AuthWrap";
import useResendEmailSignup from "@/features/auth/hooks/useResendEmailSignup";
import useSkipToProfile from "@/features/auth/hooks/useSkipToProfile";
import useVerifyEmailOtpMutation from "@/features/auth/hooks/useVerifyEmailOtpMutation";
import { cn } from "@/lib/utils";
import type { VerifyT } from "@/types/zod/common";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import MagicOrOtp from "../../features/auth/components/MagicOrOtp";

const VerifyEmail = () => {
	useSkipToProfile();
	const [isLoading, setIsLoading] = useState(true);

	const { email } = Route.useSearch();
	const resend = useResendEmailSignup();
	const verifyEmailOtpMutation = useVerifyEmailOtpMutation();

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsLoading(false);
		}, 2000);

		return () => clearTimeout(timer);
	}, []);

	const verifyEmail = async (formData: VerifyT) => {
		await verifyEmailOtpMutation.mutate({ ...formData, email: email });
	};

	return (
		<AuthWrap>
			<cc.Title words={isLoading ? "Processing" : "Enter Confirmation Code"} />

			{isLoading ? (
				<div className="space-y-4 text-center">
					<p
						className={cn(
							"mx-auto flex h-32 w-32 items-center bg-amber-200 bg-opacity-60 transition-all duration-400 justify-center mb-4 rounded-lg",
						)}
					>
						<cc.Spinner className="w-14 h-14" />
					</p>
					<div className="space-y-5">
						<p className="font-medium text-lg">Almost there! 🚀</p>
						<p className="text-md text-slate-600">
							Just a moment while we prepare your account...
						</p>
					</div>
				</div>
			) : (
				<MagicOrOtp
					email={email}
					resendFn={() => resend.mutate(email)}
					submitFn={verifyEmail}
				/>
			)}
		</AuthWrap>
	);
};

const verifyEmailSearchSchema = z.object({
	email: z.string().email(),
});

export const Route = createFileRoute("/_auth/verify")({
	component: VerifyEmail,
	validateSearch: (search) => verifyEmailSearchSchema.parse(search),
	beforeLoad: async ({ context: { auth } }) => {
		return auth;
	},
});
